import api from './api';
import { ENDPOINTS } from './endpoints';

const BLOOD_SUMMARY_PATH = ENDPOINTS.BLOOD.SUMMARY;
const BLOOD_INACTIVE_BARCODES_PATH = ENDPOINTS.BLOOD.INACTIVE_BARCODES;

function convertDateToEUFormat(dateString) {
  if (!dateString) return '';
  const parts = dateString.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }
  return dateString;
}

function mapBackendSummaryToReport(item) {
  if (!item) return null;
  const pickFirstString = (...values) => {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  };
  const splitName = (fullName) => {
    const raw = String(fullName || '').trim();
    if (!raw) return { firstName: '', lastName: '' };
    const parts = raw.split(/\s+/);
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return {
      firstName: parts.slice(0, -1).join(' '),
      lastName: parts[parts.length - 1],
    };
  };
  const customer =
    item.customer && typeof item.customer === 'object' ? item.customer : {};
  const header = {
    type: item.test_type || '',
    testDate: convertDateToEUFormat(item.test_date || ''),
    resultDate: convertDateToEUFormat(item.result_date || ''),
  };

  let adviceArray = [];
  function fallbackParseAdvice(rawStr) {
    try {
      const out = [];
      const s = rawStr
        .replace(/\r/g, '')
        .replace(/\\xa0/g, ' ')
        .replace(/\\n/g, '\n');

      const nameToken = "'advice_name': '";
      const descToken = "', 'description': '";
      const endTokens = ["'}]", "'},", "'}"];

      let cursor = 0;
      while (cursor < s.length) {
        const nameIdx = s.indexOf(nameToken, cursor);
        if (nameIdx === -1) break;
        const nameStart = nameIdx + nameToken.length;
        const descDelim = s.indexOf(descToken, nameStart);
        if (descDelim === -1) break;
        const advice_name = s.slice(nameStart, descDelim);

        const descStart = descDelim + descToken.length;

        // find earliest end token after descStart
        let endIdx = -1;
        for (const tok of endTokens) {
          const i = s.indexOf(tok, descStart);
          if (i !== -1 && (endIdx === -1 || i < endIdx)) endIdx = i;
        }
        if (endIdx === -1) break;

        const description = s.slice(descStart, endIdx);
        out.push({ advice_name, description });
        cursor = endIdx + 2; // move forward, past the closing quote
      }

      return out;
    } catch (e) {
      return [];
    }
  }
  if (Array.isArray(item.advice)) {
    adviceArray = item.advice;
  } else if (typeof item.advice === 'string') {
    const raw = item.advice.trim();
    try {
      adviceArray = JSON.parse(raw);
    } catch (_) {
      adviceArray = fallbackParseAdvice(raw);
    }
  }

  const recommendations = [];
  if (Array.isArray(adviceArray)) {
    adviceArray.forEach((a) => {
      const rawName = (a && a.advice_name) || '';
      const rawDesc = (a && a.description) || '';

      const concatMatch = rawName.match(/\n\d+\.\s*\*\*([^']+)/);
      if (concatMatch) {
        const splitIdx = concatMatch.index || 0;
        const leftPart = rawName.slice(0, splitIdx).trim();
        const rightTitle = (concatMatch[1] || '').trim().replace(/\*\*/g, '');

        let leftTitle = leftPart;
        let leftContent = '';
        const starColonIdx = leftPart.indexOf('**:');
        const colonIdx = leftPart.indexOf(':');
        if (starColonIdx !== -1) {
          leftTitle = leftPart
            .slice(0, starColonIdx)
            .replace(/\*\*/g, '')
            .trim();
          leftContent = leftPart.slice(starColonIdx + 3).trim();
        } else if (colonIdx !== -1) {
          leftTitle = leftPart.slice(0, colonIdx).replace(/\*\*/g, '').trim();
          leftContent = leftPart.slice(colonIdx + 1).trim();
        } else {
          leftTitle = leftTitle.replace(/\*\*/g, '').trim();
        }

        if (leftTitle || leftContent) {
          recommendations.push({ title: leftTitle, content: leftContent });
        }

        if (rightTitle || rawDesc) {
          recommendations.push({ title: rightTitle, content: rawDesc });
        }
      } else {
        recommendations.push({ title: rawName, content: rawDesc });
      }
    });
  }

  const results = [];
  const markers = item.markers_data || {};
  const markersRanges = item.markers_ranges || {};

  Object.keys(markers).forEach((rawName) => {
    const markerValue = markers[rawName];
    const rangeEntry = markersRanges[rawName];

    // Hide marker if no ranges info provided by backend
    if (
      !rangeEntry ||
      !Array.isArray(rangeEntry.ranges) ||
      rangeEntry.ranges.length === 0
    ) {
      return;
    }

    const sections = rangeEntry.ranges;
    const name = rangeEntry.marker_label?.trim() || rawName;
    const rawValue = markerValue;
    const currentValue = Number(rawValue);

    let currentSection = null;
    let status = 'acceptable';

    for (const section of sections) {
      if (currentValue >= section.min && currentValue <= section.max) {
        currentSection = section;
        if (section.color === 'grey') {
          status = 'attention';
        } else if (section.color === 'green') {
          status = 'optimal';
        } else if (section.color === 'blue') {
          status = 'acceptable';
        }
        break;
      }
    }

    const convertSectionsToRanges = (secs) => {
      const ranges = { min: 0, low: 0, high: 0, optimalMax: 0, max: 0 };

      const firstGreySection = secs.find((s) => s.color === 'grey');
      if (firstGreySection) {
        ranges.min = firstGreySection.min;
      }

      const firstBlueSection = secs.find((s) => s.color === 'blue');
      if (firstBlueSection) {
        ranges.low = firstBlueSection.min;
      }

      const greenSection = secs.find((s) => s.color === 'green');
      if (greenSection) {
        ranges.high = greenSection.min;
        ranges.optimalMax = greenSection.max;
      }

      const lastSection = secs[secs.length - 1];
      if (lastSection) {
        ranges.max = lastSection.max;
      }

      return ranges;
    };

    const legacyRanges = convertSectionsToRanges(sections);
    const rangesWithCurrent = { ...legacyRanges, current: currentValue };

    results.push({
      name,
      value: String(rawValue),
      unit: rangeEntry.unit || '',
      status,
      ranges: rangesWithCurrent,
      sections,
      currentSection,
      current: currentValue,
    });
  });

  const supplements = Array.isArray(item.suppliements_details)
    ? item.suppliements_details.map((s) => {
        const id = s?.id || `${s?.name || 'supp'}-${s?.brand || 'brand'}`;
        const qty = s?.quantity == null ? 1 : Number(s.quantity) || 1;
        const price = Number(s?.price) || 0;
        const dosageNum = s?.dosage != null ? String(s.dosage) : '';
        const prescription =
          s?.prescription && typeof s.prescription === 'object'
            ? {
                dosage: s.prescription.dosage ?? '',
                dosage_form:
                  s.prescription.dosage_form ??
                  s.prescription.dosageForm ??
                  '',
                form: s.prescription.form ?? '',
                daily_frequency:
                  s.prescription.daily_frequency ??
                  s.prescription.dailyFrequency ??
                  null,
                number_of_days:
                  s.prescription.number_of_days ??
                  s.prescription.numberOfDays ??
                  null,
                quantity: s.prescription.quantity ?? null,
              }
            : null;
        const content = parseInt(s?.content ?? s?.product?.content, 10) || '';
        const contentUnit =
          s?.content_unit ?? s?.product?.content_unit ?? '';
        const packagingLine =
          content && contentUnit
            ? `${content} ${contentUnit}`
            : content || contentUnit || '';
        return {
          id,
          productSku: String(
            s?.product_sku ?? s?.productSku ?? s?.sku ?? ''
          ).trim(),
          productId: s?.product_id ?? s?.productId ?? s?.id ?? null,
          sku: String(s?.product_sku ?? s?.productSku ?? s?.sku ?? '').trim(),
          brand: `${s?.name || ''}${s?.brand ? ` ${s.brand}` : ''}`.trim(),
          description: s?.description || '',
          packagingLine,
          webInfo:
            s?.web_info ??
            s?.product?.web_info ??
            s?.url ??
            s?.description ??
            '',
          image:
            s?.image_url ??
            s?.image ??
            s?.product?.image_url ??
            s?.product?.image ??
            '',
          dosage: dosageNum ? `${dosageNum} per day` : '',
          prescription,
          qty,
          price,
          checked: true,
        };
      })
    : [];

  const problems =
    Array.isArray(item.problems) && item.problems.length > 0
      ? item.problems
      : [];

  const airtablePreviewUrl =
    item.airtable_preview_link ||
    item.airtable_preview_url ||
    item.airtable_url ||
    item.airtable_link ||
    item.preview_url ||
    item.preview_link ||
    item?.airtable?.preview_url ||
    item?.airtable?.url ||
    '';

  const fullName = pickFirstString(
    customer.full_name,
    customer.fullName,
    customer.customer_name,
    customer.customerName,
    customer.patient_name,
    customer.patientName,
    customer.name,
    item.full_name,
    item.fullName,
    item.customer_name,
    item.customerName,
    item.patient_name,
    item.patientName,
    item.name
  );
  const split = splitName(fullName);
  const firstName = pickFirstString(
    customer.first_name,
    customer.firstName,
    customer.customer_first_name,
    customer.customerFirstName,
    customer.patient_first_name,
    customer.patientFirstName,
    split.firstName,
    item.first_name,
    item.firstName,
    item.customer_first_name,
    item.customerFirstName,
    item.patient_first_name,
    item.patientFirstName
  );
  const lastName = pickFirstString(
    customer.last_name,
    customer.lastName,
    customer.customer_last_name,
    customer.customerLastName,
    customer.patient_last_name,
    customer.patientLastName,
    split.lastName,
    item.last_name,
    item.lastName,
    item.customer_last_name,
    item.customerLastName,
    item.patient_last_name,
    item.patientLastName
  );
  const title = pickFirstString(
    customer.tile,
    customer.title,
    customer.salutation,
    customer.prefix,
    customer.gender_title,
    item.tile,
    item.title,
    item.salutation,
    item.prefix,
    item.gender_title
  );
  const addressLine1 = pickFirstString(
    customer.street_name,
    customer.streetName,
    customer.address_line_1,
    customer.addressLine1,
    customer.address1,
    customer.shipping_address_line1,
    customer.shipping_street_name,
    item.street_name,
    item.streetName,
    item.address_line_1,
    item.addressLine1,
    item.address1,
    item.shipping_address_line1,
    item.shipping_street_name
  );
  const streetNumber = pickFirstString(
    customer.street_number,
    customer.streetNumber,
    customer.house_number,
    customer.houseNumber,
    customer.address_number,
    customer.shipping_street_number,
    item.street_number,
    item.streetNumber,
    item.house_number,
    item.houseNumber,
    item.address_number,
    item.shipping_street_number
  );
  const postalCode = pickFirstString(
    customer.postal_code,
    customer.postalCode,
    customer.zip_code,
    customer.zipCode,
    item.postal_code,
    item.postalCode,
    item.zip_code,
    item.zipCode
  );
  const city = pickFirstString(
    customer.city,
    customer.town,
    customer.shipping_city,
    item.city,
    item.town,
    item.shipping_city
  );
  const country = pickFirstString(
    customer.country,
    customer.shipping_country,
    item.country,
    item.shipping_country
  );
  const email = pickFirstString(
    customer.shipping_email,
    customer.shippingEmail,
    customer.email,
    customer.customer_email,
    customer.customerEmail,
    customer.patient_email,
    customer.patientEmail,
    item.email,
    item.shipping_email,
    item.shippingEmail,
    item.customer_email,
    item.customerEmail,
    item.patient_email,
    item.patientEmail
  );

  return {
    orderId: item.order_number || '',
    orderApprovalId: String(item.order_id || '').trim(),
    order_id: String(item.order_id || '').trim(),
    recordId: item.blood_record_id || '',
    bloodTestId: String(
      item.blood_test_id ??
        item.bloodTestId ??
        item.blood_test ??
        item.test_id ??
        item.id ??
        ''
    ).trim(),
    blood_test_id: String(
      item.blood_test_id ??
        item.bloodTestId ??
        item.blood_test ??
        item.test_id ??
        item.id ??
        ''
    ).trim(),
    customerId:
      item.customer_id ||
      item.customerId ||
      item.customerID ||
      item.customer ||
      '',
    header,
    results,
    recommendations,
    supplements,
    problems,
    lifestyleGoals: Array.isArray(item.life_style_goals)
      ? item.life_style_goals
      : [],
    reportUrl: item.report_url || '',
    isApproved: Boolean(item.is_approved),
    approvedSupplements: Array.isArray(item.appoved_suppliements)
      ? item.appoved_suppliements
      : Array.isArray(item.approved_suppliements)
        ? item.approved_suppliements
        : [],
    supPeriodId: item.sup_period_id || '',
    // Null when no known doctor signed this test off; the doctor card is hidden then.
    doctor:
      item.doctor && typeof item.doctor === 'object'
        ? {
            name: item.doctor.name || '',
            bigNumber: item.doctor.big_number || '',
            photoUrl: item.doctor.photo_url || '',
          }
        : null,
    airtablePreviewUrl: String(airtablePreviewUrl || '').trim(),
    prescriptionPrefill: {
      clientInfo: {
        title,
        firstName,
        lastName,
      },
      shippingInfo: {
        addressLine1,
        streetNumber,
        postalCode,
        city,
        country,
        email,
      },
    },
  };
}

export async function fetchBloodSummary() {
  const { data: envelope } = await api.get(BLOOD_SUMMARY_PATH);
  const list = Array.isArray(envelope)
    ? envelope
    : Array.isArray(envelope?.data)
      ? envelope.data
      : Array.isArray(envelope?.results)
        ? envelope.results
        : Array.isArray(envelope?.payload)
          ? envelope.payload
          : [];
  return list.map(mapBackendSummaryToReport).filter(Boolean);
}

export async function fetchInactiveBarcodes(options = {}) {
  const type = String(options?.type || '').trim();
  const requestConfig = type ? { params: { type } } : undefined;
  const { data: envelope } = await api.get(BLOOD_INACTIVE_BARCODES_PATH, requestConfig);
  const list = Array.isArray(envelope?.data) ? envelope.data : [];
  return list.map((item) => ({
    id: String(item?.barcode_text || '').trim(),
    name: String(item?.type || '').trim(),
    // Numeric backend ID required by profile/barcode-activate as blood_test_id
    bloodTestId:
      item?.id == null || item?.id === ''
        ? null
        : Number.isFinite(Number(item.id))
          ? Number(item.id)
          : null,
    bloodRecordId: String(
      item?.blood_record_id || item?.blood_test_id || item?.record_id || ''
    ).trim(),
    recordId: String(
      item?.blood_record_id || item?.blood_test_id || item?.record_id || ''
    ).trim(),
    rawId: item?.id ?? null,
  }));
}

export { mapBackendSummaryToReport };
export default { fetchBloodSummary, fetchInactiveBarcodes, mapBackendSummaryToReport };

// approve supplements for a given period
export async function approveSupplements(payload) {
  // payload: { approved_suppliements: string[], sup_period_id: string }
  const path = ENDPOINTS.SUPPLEMENT.APPROVE;
  const { data } = await api.post(path, payload);
  return data;
}
